import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

import CountryFlag from "../../components/common/CountryFlag";
import { db, storage } from "../../firebase";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../../config/featureFlags";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))",
  gap: 16,
};

const CARD = {
  border: "1px solid #253654",
  borderRadius: 14,
  background: "linear-gradient(180deg, #12213a, #101b30)",
  padding: 18,
  minWidth: 0,
};

const SECTION_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#7d8fb5",
  marginBottom: 10,
  paddingBottom: 6,
  borderBottom: "1px solid #1e2f4d",
};

const ROW = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  gap: "8px 14px",
  fontSize: 13,
  alignItems: "start",
  marginBottom: 6,
};

const LABEL = { color: "#9fb2d8" };
const VALUE = { color: "#e8eefc", wordBreak: "break-word" };
const MONO = {
  ...VALUE,
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: 12,
};

const LINK_BTN = {
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid #37538f",
  background: "#182946",
  color: "#c7d7ff",
  fontSize: 12,
  fontWeight: 600,
  textDecoration: "none",
  marginRight: 8,
  marginBottom: 8,
};

function ResolvedAvatar({ raw, imgStyle, gender }) {
  const fb = defaultAvatarByGender(gender);
  const [src, setSrc] = useState(fb);

  useEffect(() => {
    let cancelled = false;
    const fallback = defaultAvatarByGender(gender);
    const v = String(raw || "").trim();
    if (!v) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    if (/^https?:\/\//i.test(v)) {
      setSrc(v);
      return () => {
        cancelled = true;
      };
    }
    let path = v;
    if (v.startsWith("gs://")) {
      const m = v.match(/^gs:\/\/[^/]+\/(.+)$/);
      path = m ? m[1] : "";
    }
    if (!path) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const url = await getDownloadURL(storageRef(storage, path));
        if (!cancelled) setSrc(url);
      } catch (e) {
        console.error("ResolvedAvatar getDownloadURL:", path, e);
        if (!cancelled) setSrc(fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, gender]);

  return (
    <img
      src={src}
      alt=""
      style={imgStyle}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = defaultAvatarByGender(gender);
      }}
    />
  );
}

function formatAnyTime(ts, msFallback) {
  if (ts && typeof ts.toDate === "function") {
    try {
      return ts.toDate().toLocaleString();
    } catch {
      /* ignore */
    }
  }
  const ms = Number(msFallback);
  if (Number.isFinite(ms) && ms > 0) return new Date(ms).toLocaleString();
  return "—";
}

function normalizeWithdrawStatus(status) {
  return String(status || "").trim().toLowerCase();
}

const STATUS_BADGE_STYLES = {
  pending: {
    background: "rgba(245, 158, 11, 0.18)",
    border: "1px solid #b45309",
    color: "#fcd34d",
  },
  under_review: {
    background: "rgba(59, 130, 246, 0.18)",
    border: "1px solid #2563eb",
    color: "#93c5fd",
  },
  approved: {
    background: "rgba(168, 85, 247, 0.18)",
    border: "1px solid #7c3aed",
    color: "#e9d5ff",
  },
  paid: {
    background: "rgba(34, 197, 94, 0.18)",
    border: "1px solid #15803d",
    color: "#86efac",
  },
  rejected: {
    background: "rgba(239, 68, 68, 0.18)",
    border: "1px solid #b91c1c",
    color: "#fca5a5",
  },
};

function getStatusBadgeStyle(status) {
  const key = normalizeWithdrawStatus(status);
  return (
    STATUS_BADGE_STYLES[key] || {
      background: "#1e293b",
      border: "1px solid #475569",
      color: "#cbd5e1",
    }
  );
}

function WithdrawStatusBadge({ status }) {
  const key = normalizeWithdrawStatus(status);
  const label = key ? key.replace(/_/g, " ") : "—";

  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "capitalize",
        ...getStatusBadgeStyle(status),
      }}
    >
      {label}
    </span>
  );
}

function getWithdrawActionVisibility(status) {
  const key = normalizeWithdrawStatus(status);

  return {
    showMoveToUnderReview: key === "pending",
    showApprove: key === "under_review",
    showReject: key === "pending" || key === "under_review",
    showMarkPaid: key === "approved",
    isTerminal: key === "paid" || key === "rejected",
    isPaid: key === "paid",
    isRejected: key === "rejected",
  };
}

const PAYOUT_BOX = {
  margin: 0,
  padding: "14px 16px",
  borderRadius: 10,
  background: "#0b1426",
  border: "1px solid #1e2f4d",
};

const PAYOUT_HEADING = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "#7d8fb5",
  marginBottom: 12,
};

const CRYPTO_PAIR = {
  fontSize: 15,
  fontWeight: 700,
  color: "#e8eefc",
  marginBottom: 10,
  letterSpacing: "0.02em",
};

const WALLET_ROW = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  flexWrap: "wrap",
};

const WALLET_TEXT = {
  flex: "1 1 200px",
  margin: 0,
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: 18,
  fontWeight: 600,
  lineHeight: 1.35,
  color: "#f0f4ff",
  wordBreak: "break-all",
};

const COPY_BTN = {
  flexShrink: 0,
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid #37538f",
  background: "#182946",
  color: "#c7d7ff",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

function CopyWalletButton({ address }) {
  return <CopyValueButton value={address} />;
}

function CopyValueButton({ value }) {
  const [copied, setCopied] = useState(false);
  const trimmed = String(value || "").trim();

  const handleCopy = () => {
    if (!trimmed || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(trimmed).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!trimmed) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      style={{
        ...COPY_BTN,
        opacity: copied ? 0.85 : 1,
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const MODAL_OVERLAY = {
  position: "fixed",
  inset: 0,
  zIndex: 1200,
  background: "rgba(5, 10, 20, 0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const MODAL_CARD = {
  width: "100%",
  maxWidth: 520,
  maxHeight: "min(90vh, 720px)",
  overflow: "auto",
  borderRadius: 14,
  border: "1px solid #37538f",
  background: "linear-gradient(180deg, #12213a, #101b30)",
  padding: 20,
  boxShadow: "0 24px 48px rgba(0, 0, 0, 0.45)",
};

const MODAL_WARNING = {
  marginBottom: 14,
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #854d0e",
  background: "rgba(120, 53, 15, 0.35)",
  color: "#fcd34d",
  fontSize: 12,
  lineHeight: 1.45,
};

const VALUE_ROW = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  flexWrap: "wrap",
};

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

function SecurePayoutFieldRow({ label, value, copyable = false }) {
  const trimmed = String(value ?? "").trim();
  const display = trimmed || "—";

  return (
    <div style={ROW}>
      <span style={LABEL}>{label}</span>
      <span style={{ ...VALUE, gridColumn: 2 }}>
        <div style={VALUE_ROW}>
          <span
            style={{
              ...(copyable ? MONO : VALUE),
              flex: "1 1 200px",
              wordBreak: "break-all",
            }}
          >
            {display}
          </span>
          {copyable && trimmed ? <CopyValueButton value={trimmed} /> : null}
        </div>
      </span>
    </div>
  );
}

function PayoutPreviewFallback({ preview }) {
  const source =
    preview && typeof preview === "object" ? preview : {};
  const entries = Object.entries(source).filter(
    ([k]) => k != null && String(k).trim() !== ""
  );
  if (entries.length === 0) {
    return <div style={{ ...VALUE, fontSize: 12 }}>—</div>;
  }
  return (
    <pre
      style={{
        margin: "8px 0 0",
        padding: 10,
        borderRadius: 8,
        background: "#0b1426",
        border: "1px solid #1e2f4d",
        color: "#b8c8e8",
        fontSize: 11,
        lineHeight: 1.45,
        overflow: "auto",
        maxHeight: 160,
      }}
    >
      {JSON.stringify(source, null, 2)}
    </pre>
  );
}

function SecurePayoutDetailsModal({
  open,
  loading,
  error,
  data,
  previewFallback,
  onClose,
}) {
  const { t } = useTranslation();

  if (!open) return null;

  const method = String(data?.method || "").toLowerCase().trim();
  const showPrivateWarning =
    Boolean(data) && data.hasPrivatePayoutDetails === false;
  const preview =
    data?.payoutPreview && typeof data.payoutPreview === "object"
      ? data.payoutPreview
      : previewFallback && typeof previewFallback === "object"
      ? previewFallback
      : {};

  return (
    <div
      style={MODAL_OVERLAY}
      role="dialog"
      aria-modal="true"
      aria-labelledby="secure-payout-modal-title"
      onClick={onClose}
    >
      <div style={MODAL_CARD} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <h2
              id="secure-payout-modal-title"
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#f3f6ff",
              }}
            >
              {t("admin.withdrawals.secure_payout_title", {
                defaultValue: "Secure payout details",
              })}
            </h2>
            {data?.withdrawId ? (
              <p style={{ ...MONO, margin: "6px 0 0", fontSize: 11 }}>
                {data.withdrawId}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              ...COPY_BTN,
              padding: "6px 12px",
            }}
          >
            {t("common.close", { defaultValue: "Close" })}
          </button>
        </div>

        {loading ? (
          <div className="admin1-state-card" style={{ margin: 0 }}>
            {t("admin.withdrawals.secure_payout_loading", {
              defaultValue: "Loading secure payout details…",
            })}
          </div>
        ) : null}

        {!loading && error ? (
          <div
            className="admin1-state-card"
            style={{ margin: 0, color: "#fca5a5" }}
          >
            {error}
          </div>
        ) : null}

        {!loading && !error && data ? (
          <>
            {showPrivateWarning ? (
              <div style={MODAL_WARNING}>
                {t("admin.withdrawals.secure_payout_private_missing", {
                  defaultValue:
                    "Private payout details are not available for this request.",
                })}
                <PayoutPreviewFallback preview={preview} />
              </div>
            ) : null}

            <SecurePayoutFieldRow
              label={t("common.status", { defaultValue: "status" })}
              value={data.status}
            />
            <SecurePayoutFieldRow
              label={t("admin.withdrawals.method", { defaultValue: "method" })}
              value={data.method}
            />
            <SecurePayoutFieldRow
              label={t("admin.fields.uid", { defaultValue: "uid" })}
              value={data.uid}
            />
            <SecurePayoutFieldRow
              label={t("admin.fields.payment_reference", {
                defaultValue: "paymentReference",
              })}
              value={data.paymentReference}
            />

            {data.firstName || data.lastName ? (
              <>
                <div style={{ ...PAYOUT_HEADING, marginTop: 12 }}>
                  {t("admin.withdrawals.recipient_details", {
                    defaultValue: "Recipient information",
                  })}
                </div>
                <SecurePayoutFieldRow
                  label={t("withdraw.recipient.firstName", {
                    defaultValue: "First name",
                  })}
                  value={data.firstName}
                />
                <SecurePayoutFieldRow
                  label={t("withdraw.recipient.lastName", {
                    defaultValue: "Last name",
                  })}
                  value={data.lastName}
                />
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.payout_country", {
                    defaultValue: "Payout country",
                  })}
                  value={data.payoutCountry}
                />
                <SecurePayoutFieldRow
                  label={t("withdraw.recipient.ownerConfirmed", {
                    defaultValue: "Owner confirmed",
                  })}
                  value={
                    data.payoutOwnerConfirmed
                      ? t("common.yes", { defaultValue: "Yes" })
                      : t("common.no", { defaultValue: "No" })
                  }
                />
                {method === "crypto" ? (
                  <SecurePayoutFieldRow
                    label={t("withdraw.recipient.cryptoIrreversible", {
                      defaultValue: "Crypto irreversible ack",
                    })}
                    value={
                      data.cryptoIrreversibleAcknowledged
                        ? t("common.yes", { defaultValue: "Yes" })
                        : t("common.no", { defaultValue: "No" })
                    }
                  />
                ) : null}
              </>
            ) : null}

            {method === "crypto" ? (
              <>
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.asset", { defaultValue: "Asset" })}
                  value={data.asset}
                />
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.network", {
                    defaultValue: "Network",
                  })}
                  value={data.network}
                />
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.wallet_address", {
                    defaultValue: "Wallet address",
                  })}
                  value={data.walletAddress}
                  copyable
                />
              </>
            ) : null}

            {method === "paypal" ? (
              <SecurePayoutFieldRow
                label={t("admin.withdrawals.paypal", { defaultValue: "PayPal" })}
                value={data.paypalEmail}
                copyable
              />
            ) : null}

            {method === "bank" ? (
              <>
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.account_holder", {
                    defaultValue: "Account holder",
                  })}
                  value={data.accountHolderName}
                />
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.bank", { defaultValue: "Bank" })}
                  value={data.bankName}
                />
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.iban", { defaultValue: "IBAN" })}
                  value={data.iban}
                  copyable
                />
                <SecurePayoutFieldRow
                  label={t("admin.withdrawals.bic", { defaultValue: "BIC" })}
                  value={data.bic}
                />
                <SecurePayoutFieldRow
                  label={t("admin.users.country", { defaultValue: "Country" })}
                  value={data.country}
                />
              </>
            ) : null}

            {!["crypto", "paypal", "bank"].includes(method) && data.payoutDetails ? (
              <div style={{ marginTop: 8 }}>
                <div style={PAYOUT_HEADING}>
                  {t("admin.withdrawals.payout", { defaultValue: "Payout" })}
                </div>
                <PayoutPreviewFallback preview={data.payoutDetails} />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function IdentitySnapshotBlock({ request }) {
  const { t } = useTranslation();
  const snap =
    request?.identitySnapshot && typeof request.identitySnapshot === "object"
      ? request.identitySnapshot
      : null;

  if (!snap) return null;

  const firstNameMasked = String(snap.firstNameMasked || "—");
  const lastNameMasked = String(snap.lastNameMasked || "—");
  const payoutCountry = String(snap.payoutCountry || "—").toUpperCase();

  return (
    <div style={PAYOUT_BOX}>
      <div style={PAYOUT_HEADING}>
        {t("admin.withdrawals.recipient_snapshot", {
          defaultValue: "Recipient attestation",
        })}
      </div>
      <div style={ROW}>
        <span style={LABEL}>
          {t("admin.withdrawals.recipient_name", {
            defaultValue: "Recipient name",
          })}
        </span>
        <span style={VALUE}>
          {firstNameMasked} {lastNameMasked}
        </span>
      </div>
      <div style={ROW}>
        <span style={LABEL}>
          {t("admin.withdrawals.payout_country", {
            defaultValue: "Payout country",
          })}
        </span>
        <span style={{ ...VALUE, display: "inline-flex", alignItems: "center" }}>
          <CountryFlag countryCode={payoutCountry} />
          <span style={{ marginInlineStart: 6 }}>{payoutCountry}</span>
        </span>
      </div>
      {snap.verificationStatus ? (
        <div style={ROW}>
          <span style={LABEL}>
            {t("admin.users.verified", { defaultValue: "Verified" })}
          </span>
          <span style={VALUE}>{String(snap.verificationStatus)}</span>
        </div>
      ) : null}
    </div>
  );
}

function PayoutDetailsBlock({ request }) {
  const { t } = useTranslation();
  const method = String(request.method || "").toLowerCase().trim();
  const pd =
    request.payoutDetails && typeof request.payoutDetails === "object"
      ? request.payoutDetails
      : {};
  const preview =
    request.payoutPreview && typeof request.payoutPreview === "object"
      ? request.payoutPreview
      : {};

  const formatFieldValue = (val) => {
    if (val == null) return "—";
    if (typeof val === "object") {
      try {
        return JSON.stringify(val, null, 2);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  if (method === "crypto") {
    const asset = String(pd.asset || preview.asset || "—").toUpperCase();
    const network = String(pd.network || preview.network || "—").toUpperCase();
    const wallet = String(pd.walletAddress || "").trim();

    return (
      <div style={PAYOUT_BOX}>
        <div style={PAYOUT_HEADING}>{t("admin.withdrawals.payout", { defaultValue: "Payout" })}</div>
        <div style={CRYPTO_PAIR}>
          {asset} ({network})
        </div>
        <div style={WALLET_ROW}>
          <p style={WALLET_TEXT}>{wallet || "—"}</p>
          {wallet ? <CopyWalletButton address={wallet} /> : null}
        </div>
      </div>
    );
  }

  if (method === "paypal") {
    const email = String(pd.paypalEmail || preview.paypalEmailMasked || "—");
    return (
      <div style={PAYOUT_BOX}>
        <div style={PAYOUT_HEADING}>{t("admin.withdrawals.payout", { defaultValue: "Payout" })}</div>
        <div style={ROW}>
          <span style={LABEL}>{t("admin.withdrawals.paypal", { defaultValue: "PayPal" })}</span>
          <span style={{ ...VALUE, wordBreak: "break-all" }}>{email}</span>
        </div>
      </div>
    );
  }

  if (method === "bank") {
    return (
      <div style={PAYOUT_BOX}>
        <div style={PAYOUT_HEADING}>{t("admin.withdrawals.payout", { defaultValue: "Payout" })}</div>
        <div style={ROW}>
          <span style={LABEL}>{t("admin.withdrawals.account_holder", { defaultValue: "Account holder" })}</span>
          <span style={VALUE}>
            {String(pd.accountHolderName || preview.accountHolderNameMasked || "—")}
          </span>
        </div>
        <div style={ROW}>
          <span style={LABEL}>{t("admin.withdrawals.bank", { defaultValue: "Bank" })}</span>
          <span style={VALUE}>
            {String(pd.bankName || preview.bankName || "—")}
          </span>
        </div>
        <div style={ROW}>
          <span style={LABEL}>{t("admin.withdrawals.iban", { defaultValue: "IBAN" })}</span>
          <span style={{ ...MONO, fontSize: 13 }}>
            {String(pd.iban || preview.ibanMasked || "—")}
          </span>
        </div>
        <div style={ROW}>
          <span style={LABEL}>{t("admin.users.country", { defaultValue: "Country" })}</span>
          <span style={{ ...VALUE, display: "inline-flex", alignItems: "center" }}>
            <CountryFlag countryCode={String(pd.country || preview.country || "")} />
          </span>
        </div>
        {(pd.bic || preview.bicMasked) && (
          <div style={ROW}>
            <span style={LABEL}>{t("admin.withdrawals.bic", { defaultValue: "BIC" })}</span>
            <span style={MONO}>{String(pd.bic || preview.bicMasked || "—")}</span>
          </div>
        )}
      </div>
    );
  }

  const fallbackSource =
    pd && Object.keys(pd).length > 0
      ? pd
      : preview && Object.keys(preview).length > 0
      ? preview
      : null;

  if (fallbackSource && typeof fallbackSource === "object") {
    const entries = Object.entries(fallbackSource).filter(
      ([k]) => k != null && String(k).trim() !== ""
    );
    if (entries.length > 0) {
      return (
        <div style={PAYOUT_BOX}>
          <div style={PAYOUT_HEADING}>{t("admin.withdrawals.payout", { defaultValue: "Payout" })}</div>
          {entries.map(([key, val]) => (
            <div style={ROW} key={String(key)}>
              <span style={LABEL}>{String(key)}</span>
              <span
                style={{
                  ...MONO,
                  fontSize: typeof val === "object" ? 11 : 13,
                  whiteSpace:
                    typeof val === "object" ? "pre-wrap" : "normal",
                }}
              >
                {formatFieldValue(val)}
              </span>
            </div>
          ))}
        </div>
      );
    }
  }

  return (
    <div style={{ ...PAYOUT_BOX, color: "#9fb2d8", fontSize: 13 }}>
      No payout details for method &quot;{request.method || "—"}&quot;.
    </div>
  );
}

export default function WithdrawRequestsPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [feedbackById, setFeedbackById] = useState({});
  const [adminNoteById, setAdminNoteById] = useState({});
  const [paymentReferenceById, setPaymentReferenceById] = useState({});
  const [payoutFetchId, setPayoutFetchId] = useState(null);
  const [payoutModal, setPayoutModal] = useState({
    open: false,
    loading: false,
    error: "",
    data: null,
    previewFallback: null,
  });

  const functions = useMemo(() => getFunctions(undefined, "us-central1"), []);
  const getWithdrawPayoutDetails = useMemo(
    () => httpsCallable(functions, "getWithdrawPayoutDetails"),
    [functions]
  );

  const closePayoutModal = useCallback(() => {
    setPayoutModal({
      open: false,
      loading: false,
      error: "",
      data: null,
      previewFallback: null,
    });
    setPayoutFetchId(null);
  }, []);

  const handleViewPayoutDetails = useCallback(
    async (request) => {
      const previewFallback =
        request?.payoutPreview && typeof request.payoutPreview === "object"
          ? request.payoutPreview
          : null;

      setPayoutFetchId(request.id);
      setPayoutModal({
        open: true,
        loading: true,
        error: "",
        data: null,
        previewFallback,
      });

      try {
        const res = await getWithdrawPayoutDetails({
          withdrawId: request.id,
        });
        setPayoutModal({
          open: true,
          loading: false,
          error: "",
          data: res?.data || null,
          previewFallback,
        });
      } catch (error) {
        console.error("getWithdrawPayoutDetails failed:", error);
        setPayoutModal({
          open: true,
          loading: false,
          error: callableErrorMessage(error),
          data: null,
          previewFallback,
        });
      } finally {
        setPayoutFetchId(null);
      }
    },
    [getWithdrawPayoutDetails]
  );

  useEffect(() => {
    const q = query(
      collection(db, "withdrawals"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((row) =>
            ["pending", "under_review", "approved"].includes(
              String(row?.status || "").trim().toLowerCase()
            )
          )
          .map((row) => {
            const snap = row.userSnapshot || {};
            const photoRaw = String(
              row.profileImage || row.photoURL || snap.photoURL || ""
            ).trim();
            return {
              id: row.id,
              uid: String(row?.uid || "—"),
              status: String(row?.status || "—"),
              method: String(row?.method || "—"),
              amountTokens: toNumber(row?.amountTokens, 0),
              requestedUsdPreview: toNumber(row?.requestedUsdPreview, 0),
              createdAtMs: Number(row?.createdAtMs || 0),
              createdAt: row.createdAt,
              reviewedAt: row.reviewedAt,
              reviewedAtMs: Number(row?.reviewedAtMs || 0),
              paidAt: row.paidAt,
              paidAtMs: Number(row?.paidAtMs || 0),
              paidBy: String(row?.paidBy ?? ""),
              adminNote: String(row?.adminNote ?? ""),
              paymentReference: String(row?.paymentReference ?? ""),
              payoutDetails: row.payoutDetails,
              payoutPreview: row.payoutPreview,
              identitySnapshot:
                row?.identitySnapshot && typeof row.identitySnapshot === "object"
                  ? row.identitySnapshot
                  : null,
              photoRaw,
              userSnapshot: {
                displayName: String(snap.displayName || "—"),
                country: String(snap.country || "—"),
                gender: String(snap.gender || ""),
              },
            };
          });

        setRequests(list);
        setLoading(false);
      },
      (error) => {
        console.error("WithdrawRequestsPage snapshot error:", error);
        setRequests([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const nf = new Intl.NumberFormat();

  async function handleReviewAction(request, action) {
    setWorkingId(request.id);
    setFeedbackById((prev) => ({ ...prev, [request.id]: "" }));

    try {
      const reviewWithdrawRequest = httpsCallable(functions, "reviewWithdrawRequest");
      await reviewWithdrawRequest({
        withdrawId: request.id,
        action,
        adminNote: String(adminNoteById[request.id] || "").trim(),
      });

      setFeedbackById((prev) => ({
        ...prev,
        [request.id]:
          action === "approved"
            ? "Request approved."
            : action === "rejected"
            ? "Request rejected."
            : "Request moved to under review.",
      }));
    } catch (error) {
      console.error("reviewWithdrawRequest failed:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [request.id]: t("admin.withdrawals.update_failed", { defaultValue: "Failed to update withdrawal request." }),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  async function handleMarkPaid(request) {
    setWorkingId(request.id);
    setFeedbackById((prev) => ({ ...prev, [request.id]: "" }));

    try {
      const markWithdrawPaid = httpsCallable(functions, "markWithdrawPaid");
      await markWithdrawPaid({
        withdrawId: request.id,
        paymentReference: String(paymentReferenceById[request.id] || "").trim(),
        adminNote: String(adminNoteById[request.id] || "").trim(),
      });

      setFeedbackById((prev) => ({
        ...prev,
        [request.id]: t("admin.withdrawals.marked_paid", { defaultValue: "Marked as paid." }),
      }));
    } catch (error) {
      console.error("markWithdrawPaid failed:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [request.id]: t("admin.withdrawals.mark_paid_failed", { defaultValue: "Failed to mark paid." }),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.withdrawals.requests_title", { defaultValue: "Withdraw Requests" })}</h1>
        <p className="admin1-dashboard__subtitle">
          Manage withdrawal documents in pending, under review, or approved state.
        </p>
      </header>

      {loading ? (
        <div className="admin1-state-card">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="admin1-state-card">{t("admin.withdrawals.no_requests", { defaultValue: "No withdrawal requests" })}</div>
      ) : (
        <section style={GRID}>
          {requests.map((request) => {
            const actions = getWithdrawActionVisibility(request.status);
            const isBusy =
              workingId === request.id || payoutFetchId === request.id;
            const hasWorkflowActions =
              actions.showMoveToUnderReview ||
              actions.showApprove ||
              actions.showReject ||
              actions.showMarkPaid;

            return (
            <article
              key={request.id}
              id={`withdraw-card-${request.id}`}
              style={CARD}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "flex-start",
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 10,
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1px solid #253654",
                      background: "#0f1a2f",
                    }}
                  >
                    <ResolvedAvatar
                      raw={request.photoRaw}
                      gender={request.userSnapshot?.gender}
                      imgStyle={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#f3f6ff",
                        marginBottom: 4,
                      }}
                    >
                      {request.userSnapshot.displayName}
                    </div>
                    <div style={{ ...MONO, marginBottom: 6 }}>
                      {request.uid}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#9fb2d8",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CountryFlag countryCode={request.userSnapshot.country} />
                    </div>
                  </div>
                </div>
                <div>
                  <Link
                    to={`/user/${encodeURIComponent(request.uid)}`}
                    style={LINK_BTN}
                  >
                    View User
                  </Link>
                  <a href={`#withdraw-card-${request.id}`} style={LINK_BTN}>
                    View Details
                  </a>
                </div>
              </div>

              <div style={SECTION_LABEL}>{t("admin.withdrawals.withdrawal", { defaultValue: "Withdrawal" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.withdrawals.withdraw_id", { defaultValue: "withdrawId" })}</span>
                <span style={MONO}>{request.id}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("common.status", { defaultValue: "status" })}</span>
                <span style={VALUE}>
                  <WithdrawStatusBadge status={request.status} />
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.withdrawals.method", { defaultValue: "method" })}</span>
                <span style={VALUE}>{request.method}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.created_at", { defaultValue: "createdAt" })}</span>
                <span style={VALUE}>
                  {formatAnyTime(request.createdAt, request.createdAtMs)}
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.reviewed_at", { defaultValue: "reviewedAt" })}</span>
                <span style={VALUE}>
                  {formatAnyTime(request.reviewedAt, request.reviewedAtMs)}
                </span>
              </div>

              <div style={{ ...SECTION_LABEL, marginTop: 12 }}>{t("admin.finance.amount", { defaultValue: "Amount" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.finance.amount_tokens", { defaultValue: "amountTokens" })}</span>
                <span style={{ ...VALUE, fontWeight: 700 }}>
                  {nf.format(request.amountTokens)}
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.finance.requested_usd_preview", { defaultValue: "requestedUsdPreview" })}</span>
                <span style={VALUE}>
                  {request.requestedUsdPreview.toFixed(2)} USD
                </span>
              </div>

              <div style={{ marginTop: 12 }}>
                <IdentitySnapshotBlock request={request} />
                <PayoutDetailsBlock request={request} />
              </div>

              <button
                type="button"
                onClick={() => handleViewPayoutDetails(request)}
                disabled={isBusy}
                style={{
                  width: "100%",
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #4c3d8f",
                  background: "linear-gradient(90deg, #3d2f6b, #2a2348)",
                  color: "#e9d5ff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: isBusy ? "not-allowed" : "pointer",
                  opacity: isBusy ? 0.7 : 1,
                }}
              >
                {payoutFetchId === request.id
                  ? t("admin.withdrawals.secure_payout_loading", {
                      defaultValue: "Loading secure payout details…",
                    })
                  : t("admin.withdrawals.view_payout_details", {
                      defaultValue: "View payout details",
                    })}
              </button>

              <div style={{ ...SECTION_LABEL, marginTop: 12 }}>{t("admin.title", { defaultValue: "Admin" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.admin_note_stored", { defaultValue: "adminNote (stored)" })}</span>
                <span style={VALUE}>{request.adminNote || "—"}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.payment_reference_stored", { defaultValue: "paymentReference (stored)" })}</span>
                <span style={MONO}>{request.paymentReference || "—"}</span>
              </div>

              {actions.isPaid ? (
                <>
                  <div style={ROW}>
                    <span style={LABEL}>{t("admin.fields.paid_at", { defaultValue: "paidAt" })}</span>
                    <span style={VALUE}>
                      {formatAnyTime(request.paidAt, request.paidAtMs)}
                    </span>
                  </div>
                  {request.paidBy ? (
                    <div style={ROW}>
                      <span style={LABEL}>{t("admin.fields.paid_by", { defaultValue: "paidBy" })}</span>
                      <span style={MONO}>{request.paidBy}</span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      marginTop: 10,
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid #15803d",
                      background: "rgba(34, 197, 94, 0.12)",
                      color: "#86efac",
                      fontSize: 12,
                    }}
                  >
                    {t("admin.withdrawals.paid_no_actions", {
                      defaultValue:
                        "This withdrawal is paid. No further actions are available.",
                    })}
                  </div>
                </>
              ) : null}

              {actions.isRejected ? (
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #b91c1c",
                    background: "rgba(239, 68, 68, 0.12)",
                    color: "#fca5a5",
                    fontSize: 12,
                  }}
                >
                  {t("admin.withdrawals.rejected_no_actions", {
                    defaultValue:
                      "This withdrawal was rejected. No further actions are available.",
                  })}
                </div>
              ) : null}

              {!actions.isTerminal ? (
                <>
              <div className="admin1-card__title" style={{ marginTop: 8 }}>
                Update admin note
              </div>
              <input
                type="text"
                value={adminNoteById[request.id] ?? ""}
                onChange={(e) =>
                  setAdminNoteById((prev) => ({
                    ...prev,
                    [request.id]: e.target.value,
                  }))
                }
                disabled={isBusy}
                placeholder={t("admin.withdrawals.admin_note_placeholder", {
                  defaultValue: "Optional admin note (sent with actions)",
                })}
                style={{
                  width: "100%",
                  background: "#0f1a2f",
                  border: "1px solid #253654",
                  color: "#d7def0",
                  borderRadius: 8,
                  padding: "9px 11px",
                  fontSize: 12,
                  marginBottom: 10,
                  opacity: isBusy ? 0.7 : 1,
                }}
              />
              {actions.showMarkPaid ? (
                <>
                  <div className="admin1-card__title">
                    {t("admin.fields.payment_reference", {
                      defaultValue: "paymentReference",
                    })}
                  </div>
                  <input
                    type="text"
                    value={paymentReferenceById[request.id] ?? ""}
                    onChange={(e) =>
                      setPaymentReferenceById((prev) => ({
                        ...prev,
                        [request.id]: e.target.value,
                      }))
                    }
                    disabled={isBusy}
                    placeholder={t(
                      "admin.withdrawals.payment_reference_placeholder",
                      {
                        defaultValue: "Optional payment reference (mark paid)",
                      }
                    )}
                    style={{
                      width: "100%",
                      background: "#0f1a2f",
                      border: "1px solid #253654",
                      color: "#d7def0",
                      borderRadius: 8,
                      padding: "9px 11px",
                      fontSize: 12,
                      opacity: isBusy ? 0.7 : 1,
                    }}
                  />
                </>
              ) : null}
                </>
              ) : null}

              {hasWorkflowActions ? (
              <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: original
                    status-based visibility (actions.showMoveToUnderReview/
                    showApprove) preserved unchanged - additionally gated
                    on the flag so these can never advance a request toward
                    payment while real-money payouts are disabled. Backend
                    also rejects independently. */}
                {actions.showMoveToUnderReview && ENABLE_REAL_MONEY_PAYOUTS ? (
                <button
                  type="button"
                  onClick={() => handleReviewAction(request, "under_review")}
                  disabled={isBusy}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #37538f",
                    background: "#1a2740",
                    color: "#f3f6ff",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isBusy ? "not-allowed" : "pointer",
                    opacity: isBusy ? 0.7 : 1,
                  }}
                >
                  Move to under review
                </button>
                ) : null}

                {actions.showApprove && ENABLE_REAL_MONEY_PAYOUTS ? (
                <button
                  type="button"
                  onClick={() => handleReviewAction(request, "approved")}
                  disabled={isBusy}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #37538f",
                    background: "linear-gradient(90deg, #4a6fb8, #37538f)",
                    color: "#f3f6ff",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isBusy ? "not-allowed" : "pointer",
                    opacity: isBusy ? 0.7 : 1,
                  }}
                >
                  Approve request
                </button>
                ) : null}

                {(actions.showMoveToUnderReview || actions.showApprove) &&
                !ENABLE_REAL_MONEY_PAYOUTS ? (
                  <div
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #37538f",
                      background: "#0f1a2f",
                      color: "#9fb2d8",
                      fontSize: 12,
                      textAlign: "center",
                    }}
                  >
                    Payout operations disabled
                  </div>
                ) : null}

                {actions.showReject ? (
                <button
                  type="button"
                  onClick={() => handleReviewAction(request, "rejected")}
                  disabled={isBusy}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #7f1d1d",
                    background: "#3f1515",
                    color: "#fecaca",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isBusy ? "not-allowed" : "pointer",
                    opacity: isBusy ? 0.7 : 1,
                  }}
                >
                  Reject request
                </button>
                ) : null}

                {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: original
                    actions.showMarkPaid visibility preserved, additionally
                    gated on the flag - this is the literal "money was
                    sent" action. Backend also rejects independently. */}
                {actions.showMarkPaid && ENABLE_REAL_MONEY_PAYOUTS ? (
                <button
                  type="button"
                  onClick={() => handleMarkPaid(request)}
                  disabled={isBusy}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #065f46",
                    background: "#064e3b",
                    color: "#a7f3d0",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isBusy ? "not-allowed" : "pointer",
                    opacity: isBusy ? 0.7 : 1,
                  }}
                >
                  Mark paid
                </button>
                ) : null}

                {actions.showMarkPaid && !ENABLE_REAL_MONEY_PAYOUTS ? (
                  <div
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #065f46",
                      background: "#0f1a2f",
                      color: "#9fb2d8",
                      fontSize: 12,
                      textAlign: "center",
                    }}
                  >
                    Payout operations disabled
                  </div>
                ) : null}
              </div>
              ) : null}

              {feedbackById[request.id] ? (
                <div className="admin1-card__note" style={{ marginTop: 10 }}>
                  {feedbackById[request.id]}
                </div>
              ) : null}
            </article>
            );
          })}
        </section>
      )}

      <SecurePayoutDetailsModal
        open={payoutModal.open}
        loading={payoutModal.loading}
        error={payoutModal.error}
        data={payoutModal.data}
        previewFallback={payoutModal.previewFallback}
        onClose={closePayoutModal}
      />
    </div>
  );
}
